import { Icons, ITagStatus } from '@tecalisdev/infinity';
import { IMetaResponse, MenuItemAction } from '../../shared/models/master.models';
import { IDocInfo, ITermsAndConditionsForm } from '../../workflows/models/workflows.models';
import {
	BOOLEAN_STATUS,
	IOperationDocumentsDetail,
	IOperationEvaluationDetail,
	IOperationOcrDetail,
	IOperationPolDetail,
	IOperationTfaCommunication,
	IPolGeneralData,
	IPolLifeTest,
	PolType,
} from '../../processes/models/processes.models';

export interface ITextLocaleMap {
	[key: string]: string;
}

export interface IOperationListResponse {
	data: IOperationListBody[];
	meta: IMetaResponse;
}
export type IOperationListBody =
	| IOperationHomeListBody
	| IOperationPolListBody
	| IOperationOcrListBody
	| IOperationDocumentsListBody
	| IOperationFormListBody
	| IOperationConnectionsListBody
	| IOperationFinancialEvaluationListBody
	| IOperationFinishListBody
	| IOperationTfaListBody;

export interface IOperationHomeListBody {
	choose: boolean;
	company: string;
	completed: boolean;
	created_at: string;
	data: string;
	details: string;
	ip: string;
	operation_type_name: OPERATION_TYPE.HOME;
	operation_type_uuid: string;
	process_uuid: string;
	state_name: string;
	state_uuid: string;
	total_time: number;
	total_tries: number;
	updated_at: string;
	uuid: string;
}
export interface IOperationPolListBody {
	auth_uuid: string;
	choose: boolean;
	company: string;
	completed: boolean;
	created_at: string;
	id_client: string;
	ip: string;
	name: ' ';
	mode?: 'manual' | 'auto';
	nationality: string;
	operation_type_name: OPERATION_TYPE.POL;
	process_uuid: string;
	state_name: string;
	state_uuid: string;
	total_time: number;
	total_tries: number;
	updated_at: string;
	uuid: string;
}

export interface IOperationOcrListBody {
	auth_uuid: string;
	choose: boolean;
	company: string;
	completed: boolean;
	created_at: string;
	doc_type: string;
	id_client: string;
	ip: string;
	name: string;
	nationality: string;
	operation_type_name: OPERATION_TYPE.OCR;
	process_uuid: string;
	state_name: string;
	state_uuid: string;
	total_time: number;
	total_tries: number;
	updated_at: string;
	uuid: string;
}

export interface IOperationFormListBody {
	choose: boolean;
	company: string;
	completed: boolean;
	created_at: string;
	id_client: string;
	ip: string;
	name: string;
	operation_type_name: OPERATION_TYPE.FORM;
	process_uuid: string;
	state_name: string;
	state_uuid: string;
	steps?: number;
	total_steps?: number;
	total_time: number;
	total_tries: number;
	updated_at: string;
	uuid: string;
}

export interface IOperationDocumentsListBody {
	auth_uuid: string;
	choose: boolean;
	company: string;
	completed: boolean;
	created_at: string;
	id_client: string;
	ip: string;
	name: string;
	nationality: string;
	operation_type_name: OPERATION_TYPE.DOCUMENTS;
	process_uuid: string;
	state_name: string;
	state_uuid: string;
	total_time: number;
	total_tries: number;
	types: { type: string }[];
	updated_at: string;
	uuid: string;
}

export interface IOperationConnectionsListBody {
	choose: boolean;
	company: string;
	completed: boolean;
	created_at: string;
	types?: { type: string }[];
	connection_type?: string;
	connection_uuid?: string;
	financial_evaluation_type?: ConnectionTypeLike | null;
	data: string;
	details: string;
	result?: string;
	id_client?: string;
	ip: string;
	name?: string;
	operation_uuid?: string;
	provider?: string;
	result_summary?: string;
	operation_type_name: OPERATION_TYPE.CONNECTIONS;
	operation_type_uuid: string;
	process_uuid: string;
	state_name: string;
	state_uuid: string;
	total_time: number;
	total_tries: number;
	updated_at: string;
	uuid: string;
}

export interface IOperationFinancialEvaluationListBody {
	auth_uuid: string;
	choose: boolean;
	company: string;
	completed: boolean;
	created_at: string;
	id_client: string;
	ip: string;
	name: string;
	nationality: string;
	operation_type_name: OPERATION_TYPE.FINANCIAL_EVALUATION;
	process_uuid: string;
	state_name: string;
	state_uuid: string;
	total_time: number;
	total_tries: number;
	financial_evaluation_type: ConnectionTypeLike | null;
	updated_at: string;
	uuid: string;
}
/** Fila de listado admin para sesiones TFA (SMS / WhatsApp vía `has_2fa`). */
/** Fila de listado TFA (`POST /operations` con has_2fa). Contrato real del back. */
export interface IOperationTfaListBody {
	id: number;
	entity: 'tfa';
	uuid: string | null;
	process_uuid: string | null;
	auth_uuid?: string | null;
	name?: string;
	nationality?: string | null;
	id_client?: string | null;
	company: string | null;
	phone?: string;
	channel?: string;
	success?: boolean;
	attempts?: number;
	expires?: string;
	state_uuid: string | null;
	state_name: string | null;
	created_at: string;
	updated_at: string;
	operation_type_name: '2FA' | OPERATION_TYPE.TFA;
}

export interface IOperationTfaSiblingSession {
	id: number;
	channel?: string;
	phone?: string;
	success?: boolean;
	created_at?: string;
	process_state_name?: string;
}

/** Payload `data` normalizado en detalle de operación TFA (mensajería). */
export interface IOperationTfaMessagingDetailData {
	phone?: string;
	channel?: string;
	success?: boolean;
	attempts?: number;
	communications?: IOperationTfaCommunication[];
	sibling_tfa_sessions?: IOperationTfaSiblingSession[];
}

/** Cuerpo de `GET /tfa/{uuid}` (admin_back, NIF-685). */
export interface IOperationTfaDetailBody {
	id?: number;
	uuid?: string;
	process_uuid: string;
	process_state_uuid?: string;
	process_state_name?: string;
	state_name?: string;
	company?: string;
	company_uuid?: string;
	name?: string;
	nationality?: string | null;
	id_client?: string | null;
	identity?: {
		uuid?: string;
		names?: string;
		surnames?: string;
		identifier?: string;
		nationality?: string | null;
	} | null;
	phone?: string;
	channel?: string;
	success?: boolean;
	attempts?: number;
	operation_type_name?: '2FA' | OPERATION_TYPE.TFA;
	communications?: IOperationTfaCommunication[];
	sibling_tfa_sessions?: IOperationTfaSiblingSession[];
	start_at?: string | null;
	end_at?: string | null;
	created_at?: string;
	updated_at?: string;
	total_time?: number;
	total_tries?: number;
	ip?: string | null;
}

/** Respuesta HTTP `GET /tfa/{uuid}` envuelta en `{ data }`. */
export interface IOperationTfaDetailApiResponse {
	data: IOperationTfaDetailBody;
}

export function isTfaListRow(row: IOperationListBody): row is IOperationTfaListBody {
	return (row as IOperationTfaListBody).entity === 'tfa';
}

export function isTfaOperationDetail(detail: IOperationDetailResponse): boolean {
	if (detail.entity === 'tfa') {
		return true;
	}
	const name = detail.operation_type_name?.toUpperCase();
	return name === OPERATION_TYPE.TFA || name === '2FA';
}

export interface IOperationFinishListBody {
	choose: boolean;
	company: string;
	completed: boolean;
	created_at: string;
	data: string;
	details: string;
	ip: string;
	operation_type_name: OPERATION_TYPE.FINISH;
	operation_type_uuid: string;
	process_uuid: string;
	state_name: string;
	state_uuid: string;
	total_time: number;
	total_tries: number;
	updated_at: string;
	uuid: string;
}

export interface IOperationListPayload {
	search?: string;

	company_uuid?: string;
	authorization_uuid?: string;
	operation_types_uuid?: string[];
	state_uuid?: string;
	process_uuid?: string;
	workflow_uuid?: string;

	// nacionality?: string;
	document_type?: string;
	connection_type?: ConnectionTypeLike;
	/** Filtro UI de canal en listado mensajería (`SMS` / `WHATSAPP`); no enviar al API. */
	messaging_channel?: string;
	/** Listado admin de sesiones TFA (mensajería). */
	has_2fa?: boolean;
	/** Canal API: `sms` | `whatsapp`. */
	channel?: string;
	provider?: string;
	connection_id?: string;
	// document_number?: string;
	// start_at?: string;
	// end_at?: string;
	sort_order?: string;
	sort_by?: string;
	per_page?: number;
	page?: number;
}

export type IOperation =
	| IHomeOperation
	| IOcrOperation
	| IPolOperation
	| IDocumentsOperation
	| IFormOperation
	| IFinishOperation
	| IChooseOperation
	| IConnectionOperation;

export enum OPERATION_TYPE {
	HOME = 'HOME',
	TFA = 'TFA',
	OCR = 'OCR',
	POL = 'POL',
	DOCUMENTS = 'DOCUMENTS',
	ATTORNEY_POWERS = 'ATTORNEY_POWERS',
	CONNECTIONS = 'CONNECTIONS',
	FORM = 'FORM',
	FINISH = 'FINISH',
	CHOOSE = 'CHOOSE',
	FINANCIAL_EVALUATION = 'FINANCIAL_EVALUATION',
	EVALUATION = 'EVALUATION',
	BORME = 'BORME',
	/** Tipos reales en API/master (`operations_types.name`). */
	SMS = 'SMS',
	WHATSAPP = 'WHATSAPP',
	/** Ruta de listado única SMS+WhatsApp (no es un tipo en API). */
	MESSAGING = 'MESSAGING',
	FULL = 'DATA', // Para podemos filtrar por todos en el listado de operaciones
}

export enum CONNECTION_TYPE {
	BANK_SCORING = 'BANK_SCORING',
	SCRIPTURE = 'SCRIPTURE',
	IRPF = 'IRPF',
	WORK_LIFE = 'WORKLIFE',
}

/**
 * Tipo de conexión tolerante a variantes del backend.
 * Incluye tanto valores del enum como claves (`WORK_LIFE`, etc.).
 */
export type ConnectionTypeLike = CONNECTION_TYPE | keyof typeof CONNECTION_TYPE;

export enum OPERATION_STATUS {
	OK = 'OK',
	KO = 'KO',
	CANCELLED = 'CANCELLED',
	INIT = 'INIT',
	NOT_STARTED = 'NOT_STARTED',
	IN_PROGRESS = 'IN_PROGRESS',
}

/** Misma semántica de UI que `INIT`: sin datos de resultado aún. */
export function isOperationStateWithoutDetailContent(state: OPERATION_STATUS | string | undefined | null): boolean {
	const s = String(state ?? '').toUpperCase();
	return s === OPERATION_STATUS.INIT || s === OPERATION_STATUS.NOT_STARTED;
}

export enum DOCS_TYPE {
	DRIVE_LICENSE = 'DRIVE_LICENSE',
	DNI = 'DNI',
	PASSPORT = 'PASSPORT',
	NIE = 'NIE',
	CERT_REG_CIU_UE = 'CERT_REG_CIU_UE',
	IRPF = 'IRPF',
	CHECK = 'CHECK',
	INVOICE = 'INVOICE',
	WORKLIFE = 'WORKLIFE',
	PAYROLL = 'PAYROLL',
	SCRIPTURE = 'SCRIPTURE',
	GENERIC = 'GENERIC',
}

export enum EVALUATION_TYPE {
	MATCH_DATA = 'match_data',
	SCORING = 'scoring',
}

export interface IHomeOperation {
	type: OPERATION_TYPE.HOME;
	config: {
		tecalis_policy_check: boolean;
		policy_checks?: ITermsAndConditionsForm[];
		description?: string;
		isLocationSaved?: boolean; // Control para el front
	};
	completed?: boolean;
	uuid?: string;
}
export interface IOcrOperation {
	type: OPERATION_TYPE.OCR;
	config: {
		iframe?: boolean;
		mode: 'manual' | 'auto';
		tutorial: boolean;
		private_mode?: boolean; // Tapar datos de documento y nombre
		allowed_docs?: IOcrAllowedDocs;
		not_allowed_docs?: IOcrAllowedDocs;
		ocr_allowed_expiration: number;
		validate_address?: boolean;
		validate_team_number?: boolean;
		midni_validation_enabled?: boolean;
		skip_pol_when_midni_authenticated?: boolean;
		eudi_wallet_enabled?: boolean;
		skip_pol_when_eudi_authenticated?: boolean;
		/** Sin límite Schengen (solo aplica a compañías con country esp). */
		is_schengen_unlimited?: boolean;
		/** Alias legacy / typo de API; el front lee ambos al cargar. */
		is_schengen_limited?: boolean;
		fraud_scoring: {
			aeat: boolean;
			legal_age: number;
			expirated: boolean;
			valid_country: boolean;
			aml: boolean;
			pep: boolean;
			manipulated_photo?: {
				blocking_step: boolean;
				ip_block: boolean;
				identifier_block: boolean;
				email_alert: string;
			};
			photocopy_check?: {
				blocking_step: boolean;
				ip_block: boolean;
				identifier_block: boolean;
				email_alert: string;
			};
			screen_photo?: {
				blocking_step: boolean;
				ip_block: boolean;
				identifier_block: boolean;
				email_alert: string;
			};
			identifier_blocked?: {
				blocking_step: boolean;
				ip_block: boolean;
				identifier_block: boolean;
				email_alert: string;
			};
		};
		match_data?: string;
		required_check_nie?: boolean;
		scan_barcode?: boolean;
		/** NIF-536: ofuscación de imagen DNI (opt-in). */
		obfuscation?: {
			fields?: {
				photo?: boolean;
				identifier?: boolean;
				address?: boolean;
				names?: boolean;
				surnames?: boolean;
				gender?: boolean;
				nationality?: boolean;
				birthdate?: boolean;
				expedition_date?: boolean;
				expiration_date?: boolean;
				support_number?: boolean;
				place_of_birth?: boolean;
				team_number?: boolean;
				parents_names?: boolean;
				mrz?: boolean;
			};
			keep_unobfuscated_copy?: boolean;
		};
		/** NIF-537: flag OCR; la marca se quema en servidor (pwcs). */
		document_watermark?: boolean;
		setting?: IOcrSetting;
		fallback?: {
			emails?: string;
			type?: 'manual';
			validation?: 'auto' | 'manual';
		};
		start_side?: 'front' | 'back';
		current_data?: any;
	};
	completed?: boolean;
	uuid?: string;
}
export interface IOcrAllowedDocs {
	passport?: Array<string>;
	dni?: Array<string>;
	nie?: boolean;
}
export interface IOcrSetting {
	total_tries: number;
	side_max_time: number;
	show_guide: boolean;
	total_request: number;
}
export interface IPolOperation {
	type: OPERATION_TYPE.POL;
	config: {
		iframe?: boolean;
		mode: 'manual' | 'auto';
		tutorial?: boolean;
		setting?: IPolSetting;
		fraud_scoring: {
			facial_recognition: boolean;
			face_match: boolean;
			percentage_similarity: number;
			anti_spoofing: boolean;
		};
		max_time: number; // seg
		fallback?: {
			emails?: string;
			type?: 'manual';
			validation?: 'auto' | 'manual';
		};
	};
	completed?: boolean;
	uuid?: string;
}

export interface IPolSetting {
	total_tries?: number;
	type?: POL_TYPE;
	available_tests?: POL_TEST[]; // 'smile', 'turn-right'
	total_test?: number;
	scoring?: number;
	max_time?: number;
}
export interface IDocumentsOperation {
	type: OPERATION_TYPE.DOCUMENTS;
	config: {
		iframe?: boolean;
		docs?: Array<IDocumentsDocs>;
		title?: string | ITextLocaleMap;
		tutorial?: boolean;
	};
	completed?: boolean;
	uuid?: string;
}

export interface IChooseOperation {
	type: OPERATION_TYPE.CHOOSE;
	config: {
		description: string;
		operations: Array<IOperation>;
	};
	completed?: boolean;
	uuid?: string;
}

export interface IConnectionOperation {
	type: OPERATION_TYPE.CONNECTIONS;
	config: {
		connection_type_uuid: any;
		type: CONNECTION_TYPE;
		fallback: {
			emails?: string;
			type?: 'manual';
			validation?: 'auto' | 'manual';
		};
		mode: 'manual' | 'auto';
		evaluation: EVALUATION_TYPE;
		total_tries: number;
	};
	completed?: boolean;
	uuid?: string;
}

export interface IFormOperation {
	type: OPERATION_TYPE.FORM;
	config: {
		tutorial?: boolean;
		/**
		 * NIF-657: `true` = OCR (y POL) antes de FORM para autocompletar;
		 * `false` = orden por defecto (FORM antes de OCR). Ausente en workflows legacy.
		 */
		autofill_from_ocr?: boolean;
		steps: Array<IFormOperationStep>;
	};
	completed?: boolean;
	uuid?: string;
}

export interface IFormOperationStep {
	uuid: string;
	order: number;
	title: string | ITextLocaleMap;
	description?: string | ITextLocaleMap;
	alert_msg?: string | ITextLocaleMap;
	fields: Array<IFormOperationField>;
}

export interface IFormOperationField {
	uuid?: string;
	id: string;
	name: string;
	type: string;
	title: string | ITextLocaleMap;
	description?: string;
	col: number;
	validators?: string[];
	multiple?: boolean;
	options?: Array<{ value: string; label: string | ITextLocaleMap }>;
}

/** Resultado de cotejo `match_ocr` por campo (contrato admin_back / pwcs_back). */
export type FormFieldMatchOcrStatus = 'OK' | 'KO';

export interface IFormFieldMatchOcrResult {
	status: FormFieldMatchOcrStatus;
	/** Valor introducido en FORM (persistido por pwcs en `filled_form_fields`). */
	form_value?: string | null;
	ocr_value?: string | null;
	checked_at?: string | null;
}

/** Comprobaciones agregadas en detalle FORM (snapshot OCR del proceso). */
export interface IOperationFormChecks {
	ocr_operation_uuid?: string | null;
	ocr?: Record<string, unknown>;
}

/** Evento de actividad por paso FORM completado en journey. */
export interface IFormStepActivityEvent {
	step_uuid: string;
	order: number;
	title: string | ITextLocaleMap;
	completed_at?: string | null;
	state?: OPERATION_STATUS | string;
	response_snapshot?: IFormOperationAnswerStep | Record<string, unknown>;
}

export interface IOperationFormDetail {
	uuid?: string;
	ip?: string;
	completed?: boolean;
	state?: OPERATION_STATUS | string;
	total_time?: number;
	total_tries?: number;
	details?: string | null;
	data?: unknown | null;
	start_at?: string | null;
	end_at?: string | null;
	current_step_order?: number;
	config?: IFormOperation['config'];
	answers?: IFormOperationAnswerStep[];
	status_reasons?: string | null;
	checks?: IOperationFormChecks;
	step_activity?: IFormStepActivityEvent[];
}

/** Paso FORM rellenado en detalle de operación/proceso (contrato journey + valor). */
export interface IFormOperationAnswerStep {
	uuid: string;
	order: number;
	title: string | ITextLocaleMap;
	description?: string | ITextLocaleMap;
	alert_msg?: string | ITextLocaleMap;
	fields: IFormOperationAnswerField[];
}

export interface IFormOperationAnswerField {
	uuid: string;
	id: string;
	name: string;
	type: string;
	title: string | ITextLocaleMap;
	col?: number;
	validators?: string[];
	value?: string | null;
	/** Opciones del campo (select/radio/checkbox) con label i18n; usadas para resolver `value` → etiqueta. */
	options?: Array<{ value: string; label: string | ITextLocaleMap }>;
	match_ocr_result?: IFormFieldMatchOcrResult;
}

export interface IFinishOperation {
	type: OPERATION_TYPE.FINISH;
	config: {
		status?: OPERATION_STATUS;
		iframe?: boolean;
		loading?: boolean;
		/** NIF-1026: mostrar motivo de error enseñable en pantalla KO (default true). */
		show_ko_reason?: boolean;
		loading_text?: string | ITextLocaleMap;
		ok_title?: string | ITextLocaleMap;
		ok_description?: string | ITextLocaleMap;
		ko_title?: string | ITextLocaleMap;
		ko_description?: string | ITextLocaleMap;
		cancelled_title?: string | ITextLocaleMap;
		cancelled_description?: string | ITextLocaleMap;
		pending_title?: string | ITextLocaleMap;
		pending_description?: string | ITextLocaleMap;
		callback?: string;
		callback_ko?: string;
		callback_cancelled?: string;
		redirect?: string;
		redirect_ko?: string;
		redirect_cancelled?: string;
	};
	completed?: boolean;
	uuid?: string;
}
export interface IDocumentsDocs {
	uuid: string;
	document_type_uuid: string;
	type: string;
	required: boolean;
	total_tries?: number;
	method: 'photo' | 'manual' | 'both' | any;
	docInfo: IDocInfo;
	min?: number;
	max?: number;
	title?: string | ITextLocaleMap;
	description?: string | ITextLocaleMap;
	instructions?: string | ITextLocaleMap;
	instructions_photo?: string | ITextLocaleMap;
	preview_validation?: boolean;
	icon: Icons;
	match_data?: any; // TODO: Tipar
}

export enum POL_TYPE {
	RANDOM = 'random',
	RANDOM_REPEAT = 'random-repeat',
	LINEAL = 'lineal',
}

export enum POL_TEST {
	SMILE = 'smile',
	TURN_RIGHT_HEAD = 'turn-right-head',
	TURN_LEFT_HEAD = 'turn-left-head',
	LIFT_HEAD = 'lift-head',
	LOWER_HEAD = 'lower-head',
	MIDDLE_FACE = 'middle-face',
	BLINK = 'blink',
	GENERAL = 'general',
}

/**
 * Formas de referencia del payload `data` en detalle de operación (`GET /operation/:id`).
 * El cuerpo real depende de `operation_type_name`; no usar como intersección única.
 */
export type IOperationDetailDataReference =
	| IOperationPolDetail
	| IOperationOcrDetail
	| IOperationDocumentsDetail
	| IOperationFormDetail
	| IOperationEvaluationDetail
	| IOperationBormeDetail;

/**
 * Payload `data` en operaciones BORME (`GET /operation/:id`).
 * Campos alineados al contrato API; propiedades adicionales vía índice.
 */
export interface IOperationBormeDetail {
	state?: OPERATION_STATUS | string;
	total_tries?: number;
	type?: string;
	mode?: 'manual' | 'auto' | string;
	details?: string | null;
	start_at?: string;
	end_at?: string;
	total_time?: number;
	tax_id?: string;
	company_name?: string;
	result_summary?: string;
	publication_reference?: string;
	[key: string]: unknown;
}

export interface IOperationDetailResponse {
	uuid: string;
	/** Origen del detalle cuando no es `GET /operation/{uuid}` estándar. */
	entity?: 'tfa';
	process_uuid: string;
	auth_uuid?: string;
	name?: string;
	nationality?: string;
	id_client?: string;
	types?: Array<{ type: string }>;
	financial_evaluation_type?: ConnectionTypeLike | null;
	doc_type?: string;
	state_uuid?: string;
	state_name?: string;
	completed?: boolean;
	total_time?: number;
	average_operation_time?: number;
	ip?: string;
	status_reasons?: string;
	details?: unknown;
	/**
	 * Payload según `operation_type_name`. Unión parcial de campos de POL/OCR/DOCUMENTS/EVAL (el API no devuelve todos a la vez).
	 * Proyectar con `as IOperationDocumentsDetail` (etc.) en código cuando el tipo sea conocido.
	 */
	data: Partial<IOperationPolDetail> &
		Partial<IOperationOcrDetail> &
		Partial<IOperationDocumentsDetail> &
		Partial<IOperationEvaluationDetail> &
		Partial<IOperationBormeDetail> &
		Record<string, unknown>;
	total_tries?: number;
	choose?: boolean;
	operation_type_uuid?: string;
	company?: string;
	company_uuid?: string;
	operation_type_name?: string;
	start_at?: string;
	end_at?: string;
	created_at?: string;
	updated_at?: string;
	logs_url?: string;
	activity?: Array<IOperationActivity>;
	identity?: {
		uuid?: string;
		names?: string;
		surnames?: string;
		identifier?: string;
		document_type?: string;
		expiration_date?: string;
		birthdate?: string;
		gender?: string;
		nationality?: string;
		country_code?: string;
	};
	locks?: {
		blocked_ips?: {
			company?: string;
			created_at?: string;
			details?: string;
			ip?: string;
			user?: string;
			uuid?: string;
		}[];
		blocked_identifiers?: {
			company?: string;
			created_at?: string;
			details?: string;
			identifier?: string;
			user?: string;
			uuid?: string;
		}[];
		blocked_faces?: {
			company?: string;
			created_at?: string;
			details?: string;
			face?: string;
			user?: string;
			uuid?: string;
		}[];
	};
	actions?: IOperationDetailAction[];
}

export interface IOperationDetailAction {
	actions_type: string;
	state: string;
	details: string;
	total_time: number;
	created_at: string;
}

export interface IOperationActivity {
	title?: string;
	icon?: Icons;
	date?: string;
	description?: string[];
	status?: BOOLEAN_STATUS;
	section?: boolean;
	/** Link a logs a nivel de fila (NIF-946). */
	showLogs?: boolean;
	tag?: {
		label: string;
		status: ITagStatus;
	};
	link?: {
		label: string;
		action: MenuItemAction;
		info: any;
		showLogs?: boolean;
	};
}

export enum TAB_OPERATIONS {
	DETAILS = 'details',
	CHECKS = 'checks',
	ACTIVITY = 'activity',
}
